import React from 'react';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import Button from '@material-ui/core/Button';
import { Checkbox } from '@material-ui/core';
import FilesystemDownloadButton from './FilesystemDownloadButton';
import { FileNode } from '../../api-client/autogenerated';
import { deleteTrashItemsByProjectId, emptyTrashByProjectId } from '../../models/api/filesystem';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';
import { ARCHIVED_PROJECT_TOOLTIP, isProjectArchived } from '../../scripts/permission-utils';
import { TooltipIfDisabledComponent } from '../custom-components/CustomButtons';
import { addSnackbar } from '../../features/snackbar/actions';

const useStyles = makeStyles(() =>
  createStyles({
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    headerText: {
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '12px',
      lineHeight: '22px',
      textTransform: 'uppercase',
    },
    disableDefaultSortIcon: {
      display: 'none',
    },
    sortIcon: {
      display: 'flex',
      flexDirection: 'column',
      height: '22px',
      paddingTop: '1px',
      marginLeft: '8px',
    },
    sortIconEnabled: {
      color: '#616061',
    },
    sortIconDisabled: {
      color: '#B2B1B2',
    },
  }),
);

export interface HeadCell {
  disablePadding: boolean;
  id: string;
  label: string;
}

interface DocumentIndexHeaderProps {
  type: 'Project Files' | 'BIM360';
  orderBy: string;
  columnTitles: HeadCell[];
  setOrderBy: (str: string) => void;
  download: () => Promise<void>;
  progress: number;
  total: number;
  selected: string[];
  children: FileNode[];
  handleSelectAll: () => void;
  disableSelectAll?: boolean;
  isRecycleBin?: boolean;
  disableInteraction?: boolean;
  /** Single panel layout: the labels wrap so five columns fit a phone without sideways scrolling. */
  mobile?: boolean;
  /**
   * A picker rather than the Drive itself. Its action columns are already gone from columnTitles;
   * this keeps the select column from reserving a checkbox's width in a header that has none.
   */
  selectionOnly?: boolean;
}

const FilesystemHeader = (props: DocumentIndexHeaderProps) => {
  const {
    type,
    orderBy,
    columnTitles,
    setOrderBy,
    download,
    progress,
    total,
    selected,
    children,
    handleSelectAll,
    disableSelectAll,
    isRecycleBin,
    disableInteraction,
    mobile,
    selectionOnly,
  } = props;

  const classes = useStyles();

  const dispatch = useDispatch();
  const project = useSelector(getProjectState);
  // Emptying the recycle bin deletes design files for good.
  const isArchivedProject = isProjectArchived(project);

  const handleDeleteTrash = async () => {
    if (!project) return;
    if (selected.length > 0) {
      const proceed = window.confirm(
        `Are you sure you want to permanently delete ${selected.length} file(s)?`,
      );
      if (!proceed) return;
      const response = await deleteTrashItemsByProjectId(project.id, selected);
      const failedDeletions = response.isSuccessfullyDeleted.filter((r) => !r);
      if (failedDeletions.length > 0) {
        dispatch(
          addSnackbar({
            id: Date.now(),
            message: `${failedDeletions.length} file(s) were not successfully deleted`,
            severity: 'error',
          }),
        );
      } else {
        dispatch(
          addSnackbar({
            id: Date.now(),
            message: `${response.isSuccessfullyDeleted} files were successfully deleted`,
            severity: 'success',
          }),
        );
      }
    } else {
      const proceed = window.confirm(
        'Are you sure you want to permanently delete every file in the Recycle Bin?',
      );
      if (!proceed) return;
      await emptyTrashByProjectId(project.id);
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: `Recycle bin emptied successfully`,
          severity: 'success',
        }),
      );
    }
  };

  const checked = children.every((n) => selected.includes(n.fullKey));
  const indeterminate = !checked && selected.length > 0;

  // One panel already drops every label, and a picker drops select-all and the action columns on
  // top of that -- what is left is a 48px band of nothing above the first row.
  if (selectionOnly && mobile) return null;

  return (
    <TableHead>
      <TableRow
        style={{
          borderTop: '1px solid #EDECEC',
          height: 48,
          pointerEvents: disableInteraction ? 'none' : 'auto',
          opacity: disableInteraction ? 0.5 : 1,
        }}
      >
        {columnTitles.map((headCell) => {
          if (headCell.id === 'select')
            return (
              <TableCell
                key={headCell.id}
                align="center"
                padding={headCell.disablePadding ? 'none' : 'default'}
                // Nothing goes here in a picker, so it claims no width either -- an empty cell of
                // a checkbox's width reads as a column the user is missing something in.
                style={selectionOnly ? { width: 0, padding: 0, border: 'none' } : undefined}
              >
                {!disableSelectAll && (
                  <Checkbox
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={handleSelectAll}
                    style={{
                      padding: '0px 0px 0px 8px',
                      display: children.length === 0 ? 'none' : 'inline-flex',
                    }}
                  />
                )}
              </TableCell>
            );
          if (headCell.id === 'view' && type !== 'BIM360') {
            // A picker keeps this column for the rows' Open Folder buttons, but has nothing of its
            // own to put above them -- downloading is not one of the choices it offers.
            if (selectionOnly) {
              return (
                <TableCell
                  key={headCell.id}
                  align="center"
                  padding="none"
                  style={{ border: 'none' }}
                />
              );
            }
            if (isRecycleBin) {
              return (
                <TableCell
                  key={headCell.id}
                  align="center"
                  padding={headCell.disablePadding ? 'none' : 'default'}
                  style={{ whiteSpace: 'nowrap', border: 'none', width: 160 }}
                >
                  <TooltipIfDisabledComponent
                    disabled={isArchivedProject}
                    title={ARCHIVED_PROJECT_TOOLTIP}
                  >
                    <Button
                      color="primary"
                      size="small"
                      disabled={isArchivedProject || children.length === 0}
                      onClick={handleDeleteTrash}
                      style={{
                        whiteSpace: 'nowrap',
                        paddingLeft: 6,
                        paddingRight: 6,
                        color: isArchivedProject ? undefined : '#ED3F26',
                      }}
                    >
                      {selected.length > 0 ? 'Delete Selected' : 'Empty Recycle Bin'}
                    </Button>
                  </TooltipIfDisabledComponent>
                </TableCell>
              );
            }
            return (
              <TableCell
                key={headCell.id}
                align="center"
                padding={headCell.disablePadding ? 'none' : 'default'}
                style={{ whiteSpace: 'nowrap', border: 'none', width: 160 }}
              >
                <FilesystemDownloadButton
                  download={download}
                  progress={progress}
                  total={total}
                  selectedCount={selected.length}
                  disabled={children.length === 0}
                />
              </TableCell>
            );
          }
          return (
            <TableCell
              key={headCell.id}
              align={
                headCell.id === 'sharing' || headCell.id === 'options' || headCell.id === 'log'
                  ? 'center'
                  : 'left'
              }
              padding={headCell.disablePadding ? 'none' : 'default'}
              style={
                mobile
                  ? { whiteSpace: 'normal', border: 'none', maxWidth: 150, padding: '0px 8px' }
                  : { whiteSpace: 'nowrap', border: 'none', maxWidth: 150 }
              }
            >
              <TableSortLabel
                onClick={() => {
                  setOrderBy(headCell.id);
                }}
                active={orderBy === headCell.id}
                classes={{
                  root: classes.headerText,
                  icon: classes.disableDefaultSortIcon,
                }}
                // Using inline style here because other approaches won't overwrite color
                style={{
                  color: '#616061',
                }}
              >
                {headCell.label}
                {headCell.label !== '' &&
                headCell.label !== 'Options' &&
                headCell.label !== 'Activity Log' ? (
                  <div className={classes.sortIcon}>
                    <FontAwesomeIcon
                      icon={faCaretUp}
                      style={{ marginBottom: '-3px' }}
                      className={
                        orderBy === headCell.id ? classes.sortIconEnabled : classes.sortIconDisabled
                      }
                    />
                    <FontAwesomeIcon
                      icon={faCaretDown}
                      style={{ marginTop: '-3px' }}
                      className={
                        orderBy === headCell.id ? classes.sortIconEnabled : classes.sortIconDisabled
                      }
                    />
                  </div>
                ) : (
                  <div />
                )}
              </TableSortLabel>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

export default FilesystemHeader;
